import type {
  QuoteHandledReason,
  QuoteHandledState,
} from 'components/Kit/HandledQuotes/types';
import {
  AutoTransportDetails,
  LTLAccessorial,
  QuoteMode,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Commodity } from 'types/Load';
import { Stop, TransportType } from 'types/QuoteRequest';
import { Maybe } from 'types/UtilityTypes';
import { QuotingPortals } from 'types/enums/Integrations';
import {
  GenericSuggestionCore,
  SuggestionCategories,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

export type SuggestedQuoteChange = GenericSuggestionCore & {
  pipeline: SuggestionPipelines.QuickQuote;
  category: SuggestionCategories.QuickQuote;
  suggested: QuoteChanges;
  applied?: QuoteChanges;
  latestEmailIDFromThread: number;
  source: QuotingPortals;
  sourceExternalID?: string;
  sourceURL?: string; // Only if portal has unique URL for each quote request
  sourceCategory: SuggestionSourceCategories;
  /** Prefixed Drumkit Quote Number (ENG-7570), e.g. DKQN123. */
  drumkitQuoteNumber?: string;
  lowBuyRate?: number;
  highBuyRate?: number;
  targetBuyRate?: number;
  // Older backends omit these fields, so Kit treats those lanes as active.
  handledState?: QuoteHandledState;
  handledReason?: Maybe<QuoteHandledReason>;
  handledAt?: Maybe<string>;
  handledByName?: Maybe<string>;
  quotedAmount?: Maybe<number>;
};

export enum SuggestionSourceCategories {
  QuotingPortal = 'quoting-portal',
  Email = 'email',
  Sharepoint = 'sharepoint',
}

export type QuoteChanges = {
  mode: QuoteMode;
  customerExternalTMSID: string;
  transportType: TransportType;
  pickupCity: string;
  pickupState: string;
  pickupZip: string;
  pickupDate: Maybe<Date>;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  deliveryDate: Maybe<Date>;
  estimatedKMATransitDays?: Maybe<number>;
  fuelSurchargePerMile: Maybe<number>;
  fuelSurchargeTotal: Maybe<number>;
  distanceMiles: Maybe<number>;
  stops?: Array<Stop>;
  autoTransport?: AutoTransportDetails;
  accessorials?: Array<LTLAccessorial>;
  commodities?: Array<Commodity>;
  // Set when the shipment came from a multi-shipment LTL email.
  batchLtlShipment?: Maybe<BatchLTLShipmentDetails>;
};

// How the shipper listed one shipment of a multi-shipment LTL email.
export type BatchLTLShipmentDetails = {
  // 1-based position in the email.
  index: number;
  // The shipper's own name for the shipment, e.g. "Invoice 68033" or "(2)".
  label: string;
  reference?: string;
  notes?: string;
  // Extraction hint only; the form's validation decides what is missing.
  missingFields?: string[];
};
